import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Brush as BrushIcon,
  Receipt as ReceiptIcon,
  Agriculture as AgricultureIcon,
  Build as BuildIcon,
  AccountBalance as AccountBalanceIcon,
  ViewModule as CardViewIcon,
  ViewList as RowViewIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Verified as VerifiedIcon,
  Shield as ShieldIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { projectService, Project, PlatformStats } from '../services/projectService';
import { useThemeMode } from '../contexts/ThemeContext';
import ProjectCard from '../components/ProjectCard';
import InvestmentModal from '../components/InvestmentModal';
import Loader from '../components/Loader';

const Home = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewMode, setViewMode] = useState<'card' | 'row'>('card');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [investmentModalOpen, setInvestmentModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedAssetType, setSelectedAssetType] = useState<string | null>(null);

  const getAssetIcon = (projectType: string) => {
    switch (projectType) {
      case 'real_estate':
        return <HomeIcon />;
      case 'art':
        return <BrushIcon />;
      case 'invoice':
        return <ReceiptIcon />;
      case 'agriculture':
        return <AgricultureIcon />;
      case 'equipment':
        return <BuildIcon />;
      default:
        return <AccountBalanceIcon />;
    }
  };

  const formatProjectType = (projectType: string) => {
    return projectType.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const toggleRowExpansion = (projectId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(projectId)) {
      newExpandedRows.delete(projectId);
    } else {
      newExpandedRows.add(projectId);
    }
    setExpandedRows(newExpandedRows);
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await projectService.getProjects();
        setAllProjects(response.projects);

        const tokenizedProjects = response.projects.filter(p => p.status === 'tokenized' || p.status === 'approved');
        if (tokenizedProjects.length === 0) {
          setFeaturedProjects(response.projects.slice(0, 6));
        } else {
          setFeaturedProjects(tokenizedProjects.slice(0, 6));
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Calculate real platform stats from projects
  const platformStats: PlatformStats = useMemo(() => {
    return projectService.calculatePlatformStats(allProjects);
  }, [allProjects]);

  // Filter projects by selected asset type
  const displayedProjects = useMemo(() => {
    if (!selectedAssetType) return featuredProjects;
    return featuredProjects.filter(p => p.project_type === selectedAssetType);
  }, [featuredProjects, selectedAssetType]);

  const stats = useMemo(() => [
    {
      label: 'Total Tokenized Volume',
      value: formatCurrency(platformStats.totalTokenizedVolume),
      icon: TrendingUpIcon,
      color: '#3B82F6',
      bgColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
      borderColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
    },
    {
      label: 'Active Users',
      value: platformStats.activeInvestors.toString(),
      icon: PeopleIcon,
      color: '#10B981',
      bgColor: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
      borderColor: isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
    },
    {
      label: 'Tokenized Assets',
      value: platformStats.tokenizedProjects.toString(),
      icon: BusinessIcon,
      color: '#0EA5E9',
      bgColor: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.08)',
      borderColor: isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.15)',
    },
    {
      label: 'Total Projects',
      value: platformStats.totalProjects.toString(),
      icon: InventoryIcon,
      color: '#F59E0B',
      bgColor: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.08)',
      borderColor: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
    },
  ], [platformStats, isDark]);

  const assetTypes = [
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'art', label: 'Art' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'agriculture', label: 'Agriculture' },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(displayedProjects.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? Math.ceil(displayedProjects.length / 3) - 1 : prev - 1
    );
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newViewMode: 'card' | 'row' | null) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleAssetTypeClick = (type: string) => {
    if (selectedAssetType === type) {
      setSelectedAssetType(null);
    } else {
      setSelectedAssetType(type);
    }
    setCurrentSlide(0);
  };

  const handleInvest = async (project: Project) => {
    setSelectedProject(project);
    setInvestmentModalOpen(true);
  };

  const handleInvestmentModalClose = () => {
    setInvestmentModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(180deg, rgba(30, 64, 175, 0.15) 0%, rgba(11, 17, 32, 1) 100%)'
            : 'linear-gradient(180deg, rgba(30, 64, 175, 0.08) 0%, rgba(248, 250, 252, 1) 100%)',
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${isDark ? 'rgba(59, 130, 246, 0.03)' : 'rgba(59, 130, 246, 0.05)'} 1px, transparent 1px),
              linear-gradient(90deg, ${isDark ? 'rgba(59, 130, 246, 0.03)' : 'rgba(59, 130, 246, 0.05)'} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Badge */}
                <Chip
                  icon={<VerifiedIcon sx={{ fontSize: '0.875rem' }} />}
                  label="Institutional-Grade Security"
                  sx={{
                    mb: 3,
                    background: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
                    border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                    color: isDark ? '#60A5FA' : '#2563EB',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': { color: isDark ? '#60A5FA' : '#2563EB' },
                  }}
                />

                {/* Headline */}
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '2.25rem', md: '3rem' },
                    lineHeight: 1.2,
                    mb: 2,
                    color: 'text.primary',
                  }}
                >
                  Real World Asset
                  <br />
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #3B82F6, #0EA5E9)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Tokenization Platform
                  </Box>
                </Typography>

                {/* Subheadline */}
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    mb: 4,
                    maxWidth: '500px',
                  }}
                >
                  Transform real-world assets into digital securities with compliant tokenization,
                  institutional-grade custody, and seamless blockchain integration.
                </Typography>

                {/* CTA Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/submit')}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    Tokenize Your Asset
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/dashboard/investor')}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    Explore Investments
                  </Button>
                </Box>
              </motion.div>
            </Grid>

            {/* Stats Card */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card
                  sx={{
                    background: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <ShieldIcon sx={{ color: '#3B82F6', fontSize: '1.25rem' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Platform Metrics
                      </Typography>
                      {loading && (
                        <Chip
                          label="Loading..."
                          size="small"
                          sx={{
                            ml: 'auto',
                            fontSize: '0.65rem',
                            height: 20,
                            background: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                          }}
                        />
                      )}
                    </Box>

                    <Grid container spacing={3}>
                      {stats.map((stat, index) => (
                        <Grid item xs={6} key={index}>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                background: stat.bgColor,
                                border: `1px solid ${stat.borderColor}`,
                                textAlign: 'center',
                              }}
                            >
                              <stat.icon sx={{ fontSize: '1.5rem', color: stat.color, mb: 1 }} />
                              {loading ? (
                                <Skeleton
                                  variant="text"
                                  width="60%"
                                  height={32}
                                  sx={{ mx: 'auto', bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
                                />
                              ) : (
                                <Typography
                                  variant="h5"
                                  sx={{ fontWeight: 700, color: stat.color, mb: 0.5 }}
                                >
                                  {stat.value}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                              >
                                {stat.label}
                              </Typography>
                            </Box>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Featured Projects Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#3B82F6',
                fontWeight: 600,
                letterSpacing: '0.15em',
                mb: 1,
                display: 'block',
              }}
            >
              Investment Opportunities
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
            >
              Featured Assets
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}
            >
              Discover vetted real-world assets available for fractional investment
            </Typography>
          </Box>

          {/* Asset Type Filters */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 4 }}>
            {assetTypes.map((type) => (
              <Chip
                key={type.value}
                label={type.label}
                size="small"
                onClick={() => handleAssetTypeClick(type.value)}
                sx={{
                  cursor: 'pointer',
                  background: selectedAssetType === type.value
                    ? 'rgba(59, 130, 246, 0.2)'
                    : isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${selectedAssetType === type.value
                    ? 'rgba(59, 130, 246, 0.5)'
                    : isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  color: selectedAssetType === type.value
                    ? '#3B82F6'
                    : isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                  fontWeight: selectedAssetType === type.value ? 600 : 400,
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: '#3B82F6',
                  },
                }}
              />
            ))}
            {selectedAssetType && (
              <Chip
                label="Clear Filter"
                size="small"
                onClick={() => setSelectedAssetType(null)}
                sx={{
                  cursor: 'pointer',
                  background: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
                  color: '#EF4444',
                  '&:hover': {
                    background: 'rgba(239, 68, 68, 0.2)',
                  },
                }}
              />
            )}
          </Box>

          {/* View Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                  border: 'none',
                  px: 2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                    color: '#FFF',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                    },
                  },
                  '&:hover': {
                    background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                },
              }}
            >
              <ToggleButton value="card">
                <CardViewIcon sx={{ mr: 1, fontSize: '1rem' }} />
                Cards
              </ToggleButton>
              <ToggleButton value="row">
                <RowViewIcon sx={{ mr: 1, fontSize: '1rem' }} />
                List
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </motion.div>

        {/* Projects Display */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Loader size={60} variant="walk" text="Loading assets..." />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {error}
            </Typography>
            <Button variant="outlined" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
              Try Again
            </Button>
          </Box>
        ) : displayedProjects.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8, background: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.8)' }}>
            <CardContent>
              <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {selectedAssetType ? `No ${formatProjectType(selectedAssetType)} assets available yet` : 'No assets available yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Be the first to tokenize your real-world assets on our platform
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/submit')}
              >
                Submit Your Asset
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ position: 'relative', minHeight: viewMode === 'card' ? '500px' : 'auto' }}>
            {viewMode === 'card' ? (
              <Grid container spacing={3}>
                {displayedProjects
                  .slice(currentSlide * 3, (currentSlide + 1) * 3)
                  .map((project, index) => (
                    <Grid item xs={12} md={4} key={project.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        style={{ height: '100%' }}
                      >
                        <ProjectCard project={project} onInvest={handleInvest} />
                      </motion.div>
                    </Grid>
                  ))}
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {displayedProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Card
                      sx={{
                        background: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'rgba(59, 130, 246, 0.3)',
                          background: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                          {/* Asset Info */}
                          <Grid item xs={12} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#FFF',
                                }}
                              >
                                {getAssetIcon(project.project_type)}
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {project.title}
                                </Typography>
                                <Chip
                                  label={project.status.toUpperCase()}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    background:
                                      project.status === 'tokenized'
                                        ? 'rgba(16, 185, 129, 0.2)'
                                        : 'rgba(59, 130, 246, 0.2)',
                                    color:
                                      project.status === 'tokenized' ? '#10B981' : '#3B82F6',
                                    border: `1px solid ${project.status === 'tokenized' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>

                          {/* Type */}
                          <Grid item xs={6} md={2}>
                            <Typography variant="caption" color="text.secondary">Type</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                              {formatProjectType(project.project_type)}
                            </Typography>
                          </Grid>

                          {/* Location */}
                          <Grid item xs={6} md={2}>
                            <Typography variant="caption" color="text.secondary">Location</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                              {project.location || 'Global'}
                            </Typography>
                          </Grid>

                          {/* Valuation */}
                          <Grid item xs={6} md={2}>
                            <Typography variant="caption" color="text.secondary">Valuation</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B82F6' }}>
                              {formatCurrency(project.valuation_usd || 0)}
                            </Typography>
                          </Grid>

                          {/* Tokenized */}
                          <Grid item xs={6} md={1}>
                            <Typography variant="caption" color="text.secondary">Tokenized</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#10B981' }}>
                              {project.tokenized_percent || 0}%
                            </Typography>
                          </Grid>

                          {/* Actions */}
                          <Grid item xs={12} md={2}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleInvest(project)}
                                disabled={project.status !== 'tokenized'}
                                sx={{
                                  fontSize: '0.75rem',
                                  px: 2,
                                }}
                              >
                                {project.status === 'tokenized' ? 'Invest' : 'Coming Soon'}
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpansion(project.id)}
                                sx={{
                                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                                  color: 'text.secondary',
                                  '&:hover': {
                                    borderColor: 'rgba(59, 130, 246, 0.3)',
                                    color: '#3B82F6',
                                  },
                                }}
                              >
                                {expandedRows.has(project.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Expanded Details */}
                        {expandedRows.has(project.id) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                          >
                            <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                                  Description
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                  {project.description}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {project.token_name && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Token</Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {project.token_name} ({project.token_symbol})
                                      </Typography>
                                    </Box>
                                  )}
                                  {project.created_at && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Created</Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {new Date(project.created_at).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          </motion.div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </Box>
            )}

            {/* Navigation Arrows for Card View */}
            {viewMode === 'card' && displayedProjects.length > 3 && (
              <>
                <IconButton
                  onClick={prevSlide}
                  sx={{
                    position: 'absolute',
                    left: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    color: 'text.secondary',
                    '&:hover': {
                      background: isDark ? 'rgba(17, 24, 39, 1)' : 'rgba(255, 255, 255, 1)',
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                      color: '#3B82F6',
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <IconButton
                  onClick={nextSlide}
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    color: 'text.secondary',
                    '&:hover': {
                      background: isDark ? 'rgba(17, 24, 39, 1)' : 'rgba(255, 255, 255, 1)',
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                      color: '#3B82F6',
                    },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </>
            )}
          </Box>
        )}

        {/* Investment Modal */}
        <InvestmentModal
          open={investmentModalOpen}
          onClose={handleInvestmentModalClose}
          project={selectedProject}
        />
      </Container>
    </Box>
  );
};

export default Home;
